import React, { Fragment, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../util/axiosInstance";
import axios from "axios";
import { Dialog, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import { getUIDescription } from "../../../components/SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";
interface Unit {
  id: string;
  name: string;
  [key: string]: any;
}

interface Group {
  id?: string;
  name?: string;
}

interface ReportGroupUnitsProps {
  group: Group | null;
}

export default function ReportGroupUnits({ group }: ReportGroupUnitsProps) {
  const [loading, setLoading] = useState(false);
  const [groupUnits, setGroupUnits] = useState<Unit[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<"Unit Name" | "Unit ID">("Unit Name");
  const [searchQuery, setSearchQuery] = useState("");

  const [assignUnitDialogOpen, setAssignUnitDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<string[]>([]);

  const API = process.env.REACT_APP_API_GATEAWAY || "";

  const fetchUnits = async () => {
    if (!group?.id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.get(`${API}/devices?groupId=${group.id}`);
      setGroupUnits(res.data || []);
    } catch (err) {
      console.error("Error fetching units:", err);
      setError("Failed to load units.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, [group?.id]);

  const handleRequestSort = (property: "Unit Name" | "Unit ID") => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedUnitsData = useMemo(() => {
    if (!groupUnits) return [] as Unit[];

    return [...groupUnits].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (orderBy) {
        case "Unit Name":
          aValue = a.name;
          bValue = b.name;
          break;
        case "Unit ID":
          aValue = a.id;
          bValue = b.id;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [groupUnits, order, orderBy]);

  const filteredUnitsData = sortedUnitsData.filter(
    (row) =>
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpenAssignUnitDialog = async () => {
    setAssignUnitDialogOpen(true);
  };

  const handleDeleteClick = (unit: Unit) => {
    setUnitToDelete((prevUnits) => [...prevUnits, unit.id]);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (unitToDelete.length === 0) return;
    try {
      setConfirmOpen(false);
      const response = await axiosInstance.delete(
        `${API}/assets/group/${group?.id}`,
        {
          data: { deviceIds: unitToDelete,UI_Description: `${getUIDescription()}\n Delete unit From Report Group`, },
        }
      );
      if (response.status >= 200 && response.status < 300) {
        toast.success("Unit Successfully Deleted from Report Group");
        await fetchUnits();
        setUnitToDelete([]);
      } else {
        toast.error("Failed to delete unit from report group");
        console.error("Failed to delete unit from report group");
      }
    } catch (err) {
      console.error("❌ Error deleting unit from report group:", err);
    }
  };

  return (
    <div className="mt-2 mx-2">
      {loading ? (
        <div className="w-full min-h-[200px] flex justify-center items-center relative z-10">
          <Spinner size={16} />
        </div>
      ) : error ? (
        <div className="w-full text-center text-red-600 font-bold py-2">
          {error}
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Search Unit..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full md:w-[300px] px-3 py-2 rounded bg-slate-800 text-white border border-slate-600"
                aria-label="Search Unit"
              />
            </div>

            <div className="flex items-center gap-2 justify-end">
              <button
                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={fetchUnits}
              >
                Refresh
              </button>
              <button
                className="px-3 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50"
                onClick={handleOpenAssignUnitDialog}
              >
                Assign Unit
              </button>
            </div>
          </div>

          <div className="overflow-auto max-h-[63vh] px-2 mt-5 rounded-lg">
            <div className="rounded-xl overflow-hidden border border-gray-700">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 bg-gray-100">
                  <tr className="border-b border-slate-700 bg-slate-800">
                    {(["Unit Name", "Unit ID", "Delete"] as const).map(
                      (col) => (
                        <th
                          key={col}
                          className="p-2 cursor-pointer select-none font-semibold"
                        >
                          {col !== "Delete" ? (
                            <button
                              className="inline-flex items-center gap-2"
                              onClick={() =>
                                handleRequestSort(
                                  col as "Unit Name" | "Unit ID"
                                )
                              }
                            >
                              <span>{col}</span>
                              <SortIcon
                                active={orderBy === col}
                                direction={order}
                              />
                            </button>
                          ) : (
                            <span>{col}</span>
                          )}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredUnitsData.length > 0 ? (
                    filteredUnitsData.map((unit, index) => (
                      <tr
                        key={unit.id}
                        className={
                          index % 2 === 0
                            ? "bg-gray-800 hover:bg-gray-700"
                            : "bg-gray-700 hover:bg-gray-600"
                        }
                      >
                        <td className="px-4 py-3 text-sm">{unit.name}</td>
                        <td className="px-4 py-3 text-sm">{unit.id}</td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                            onClick={() => handleDeleteClick(unit)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="py-6 text-center">
                        No units found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <AssignUnitDialog
        open={assignUnitDialogOpen}
        onClose={() => setAssignUnitDialogOpen(false)}
        groupId={group?.id}
        groupName={group?.name}
        onAssigned={fetchUnits}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

/* ----------------------------- Helper components ---------------------------- */

function Spinner({ size = 24 }: { size?: number }) {
  return (
    <div
      className="inline-block animate-spin"
      style={{ width: size * 2, height: size * 2 }}
      role="status"
      aria-label="loading"
    >
      <svg
        className="w-full h-full"
        viewBox="0 0 50 50"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          strokeWidth="5"
          strokeOpacity="0.2"
        />
        <path
          fill="none"
          strokeWidth="5"
          d="M45 25a20 20 0 0 1-20 20"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function SortIcon({
  active,
  direction,
}: {
  active: boolean;
  direction: "asc" | "desc";
}) {
  return (
    <svg
      className={`w-3 h-3 transform ${active ? "opacity-100" : "opacity-40"}`}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 9l6-6 6 6"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 15l6 6 6-6"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ----------------------------- AssignUnitDialog ---------------------------- */

function AssignUnitDialog({
  open,
  onClose,
  groupId,
  groupName,
  onAssigned,
}: {
  open: boolean;
  onClose: () => void;
  groupId?: string;
  groupName?: string;
  onAssigned?: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<Unit[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState(""); // <-- NEW
  const API = process.env.REACT_APP_API_GATEAWAY || "";

  useEffect(() => {
    if (!open) return;
    const fetchCandidates = async () => {
      if (!groupId) return setCandidates([]);
      try {
        setLoading(true);
        const res = await axiosInstance.get(`${API}/devices`);
        setCandidates(res.data || []);
      } catch (err) {
        console.error("Error fetching candidates:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCandidates();
  }, [open]);

  const toggleSelect = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (!groupId || selected.length === 0) {
      toast.warning("Please select at least one unit");
      return;
    }

    try {
      const payload = { deviceIds: selected, groupId,UI_Description: `${getUIDescription()}\n Assign Unit to Report Group`, };
      console.log(payload);
      await axiosInstance.post(
        `${process.env.REACT_APP_API_GATEAWAY}/assets/createMany/`,
        payload
      );
      toast.success("Successfully Assigned Units to Group Report!");
      setSelected([]);
      onClose(); // <-- call it only once
      onAssigned && onAssigned();
    } catch (err) {
      console.error("❌ Error assigning unit to report group:", err);
      toast.error("Failed to assign unit to report group");
    }
  };

  // NEW — filtered list
  const filteredCandidates = candidates.filter((c) => {
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.id?.toLowerCase().includes(q);
  });

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-4 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded bg-slate-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-white"
                >
                  Assign Unit to {groupName}
                </Dialog.Title>

                {/* 🔍 Search Bar */}
                <div className="mt-4">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or ID..."
                    className="w-full rounded border border-gray-600 bg-slate-700 px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* List */}
                <div className="mt-4">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Spinner />
                    </div>
                  ) : filteredCandidates.length === 0 ? (
                    <div className="py-6 text-center text-sm text-gray-300">
                      No matching units
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-auto">
                      {filteredCandidates.map((c) => (
                        <label
                          key={c.id}
                          className={`flex items-center gap-3 p-2 rounded border cursor-pointer hover:bg-slate-600 ${
                            selected.includes(c.id)
                              ? "bg-blue-700 border-blue-400"
                              : "bg-slate-700 border-gray-700"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(c.id)}
                            onChange={() => toggleSelect(c.id)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 text-sm text-white">
                            <div className="font-medium">{c.name}</div>
                            <div className="text-xs text-gray-400">{c.id}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                    onClick={() => {
                      setSelected([]);
                      setSearch("");
                      onClose();
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={handleAssign}
                  >
                    Assign ({selected.length})
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

/* ------------------------------ ConfirmDialog ------------------------------ */

function ConfirmDialog({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-4 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded bg-slate-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-white-900"
                >
                  Confirm
                </Dialog.Title>

                <div className="mt-2">
                  <p className="text-sm text-white-500">
                    Are you sure you want to delete the selected unit(s)?
                  </p>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-900 rounded hover:bg-gray-300"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    onClick={onConfirm}
                  >
                    Delete
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
